// Пайплайн разбора партии: двухпроходный прогон движка по главной линии
// (быстрый по всем + дотяжка ключевых моментов), детект «Блестящих».
// Порт analyzeAllPlies/keyMomentNodes/detectBrilliants из app.js v3 (~2080–2382).
// Движок инжектится — модуль тестируется с фейковым analyze.
import type { GameNode } from './types';
import type { EngineAnalysis } from './eval-utils';
import { EvalStore, evalKey, nodeFen, engineArtifactsValid, setEngineArtifacts } from './evals';
import { mainLineOf } from './tree';
import { brilliantGates, type ClassifyCtx } from './classify';

export type AnalyzeFn = (
  fen: string,
  opts: { depth: number; multipv: number; movetimeMs?: number },
) => Promise<EngineAnalysis>;

export interface ReviewProgress {
  stage: 'quick' | 'deep' | 'brilliant';
  done: number;
  total: number;
}

export interface ReviewOptions {
  analyze: AnalyzeFn;
  evals: EvalStore;
  /** Полная глубина разбора (дотяжка ключевых моментов). */
  depth: number;
  onProgress?: (p: ReviewProgress) => void;
  /** false → разбор прерван (началась другая партия). */
  isCurrent?: () => boolean;
  /** Порог «ключевого момента» в cp (перепад оценки). */
  keyMomentCp?: number;
}

/** Прогнать движок по всем позициям главной линии. Заполняет evals + артефакты узлов. */
export async function reviewGame(root: GameNode, opts: ReviewOptions): Promise<void> {
  const { analyze, evals } = opts;
  const isCurrent = opts.isCurrent || (() => true);
  const fullDepth = opts.depth;
  const quickDepth = Math.min(12, fullDepth);
  const nodes: GameNode[] = [root, ...mainLineOf(root)];
  const total = nodes.length;

  // Проход 1: быстрый по всей партии — аннотации появляются почти сразу.
  let done = 0;
  for (const n of nodes) {
    if (!isCurrent()) return;
    const fen = nodeFen(n);
    if (!evals.has(fen) || !engineArtifactsValid(n, quickDepth) || !n._pv || n._bestmove == null || n._secondCp == null) {
      try {
        const a = await analyze(fen, { depth: quickDepth, multipv: 2, movetimeMs: 2000 });
        evals.cacheAnalysis(fen, a);
        setEngineArtifacts(n, a, quickDepth);
      } catch {
        /* пропускаем позицию */
      }
    }
    done++;
    opts.onProgress?.({ stage: 'quick', done, total });
  }

  // Проход 2: дотягиваем на полную глубину только ключевые моменты.
  if (fullDepth > quickDepth) {
    const key = keyMomentNodes(nodes, evals, opts.keyMomentCp ?? 90);
    let deepDone = 0;
    for (const n of key) {
      if (!isCurrent()) return;
      const fen = nodeFen(n);
      try {
        const a = await analyze(fen, { depth: fullDepth, multipv: 2 });
        evals.cacheAnalysis(fen, a);
        setEngineArtifacts(n, a, fullDepth);
      } catch {
        /* пропускаем позицию */
      }
      deepDone++;
      opts.onProgress?.({ stage: 'deep', done: deepDone, total: key.length });
    }
  }

  if (isCurrent()) await detectBrilliants(nodes.slice(1), opts);
}

/** Позиции вокруг крупных перепадов оценки (после быстрого прохода). */
export function keyMomentNodes(nodes: GameNode[], evals: EvalStore, thresholdCp = 90): GameNode[] {
  const key = new Set<GameNode>();
  let prevCp: number | null = null;
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i]!;
    const cp = evals.get(evalKey(nodeFen(n)));
    if (cp != null && prevCp != null && Math.abs(cp - prevCp) >= thresholdCp) {
      if (nodes[i - 1]) key.add(nodes[i - 1]!); // позиция ДО хода — для bestmove/второй линии
      key.add(n);
    }
    if (cp != null) prevCp = cp;
  }
  return [...key];
}

/** Кандидаты в «Блестящие» перепроверяются на depth+2 (cap 24). */
export async function detectBrilliants(nodes: GameNode[], opts: ReviewOptions): Promise<void> {
  const isCurrent = opts.isCurrent || (() => true);
  const ctx: ClassifyCtx = { evals: opts.evals, depth: opts.depth };
  let checked = 0;
  for (const node of nodes) {
    if (!isCurrent()) return;
    node._brilliant = false;
    if (!brilliantGates(node, ctx)) continue;
    const p = node.parent!;
    const fen = nodeFen(p);
    try {
      const depth = Math.min(opts.depth + 2, 24);
      const a2 = await opts.analyze(fen, { depth, multipv: 2 });
      opts.evals.cacheAnalysis(fen, a2);
      setEngineArtifacts(p, a2, depth);
      node._brilliant = brilliantGates(node, { evals: opts.evals, depth });
    } catch {
      node._brilliant = false;
    }
    checked++;
    opts.onProgress?.({ stage: 'brilliant', done: checked, total: nodes.length });
  }
}
